import { Button, PageHeader, Space } from 'antd'
import React from 'react'
import TableBlock from '../../components/tables/TableBlock'
import { getFixedDesigns } from '../../network/FixedDesign'
import { ROLE_PERMISSIONS } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'
import { createUrlWithFiltersFromKeyRequest } from '../../utils/filterUtils'
import useSearchParams from '../../hooks/useQuery'
import { formOptions } from '../../forms/blocks'

/** Normalize language-string (string or { en, zh }) to a single display string */
const nameLabel = (name) =>
  typeof name === 'string' ? name : (name?.en || name?.zh || '')

/** Category = first object key in details[0] (e.g. belt, vest) */
const getCategoryFromDetails = (details) => {
  const first = details?.[0]
  if (!first || typeof first !== 'object') return null
  const key = Object.keys(first).filter((k) => k !== '_id')[0]
  return key || null
}

/** Normalize store name (string or { en, zh }) to a single display string */
const storeNameLabel = (name) =>
  typeof name === 'string' ? name : (name?.en || name?.zh || '')

/** Category filter options: same keys as CreateFixedDesign (exclude collection, suit_2_pieces, suit_3_pieces, default) */
const CATEGORY_FILTER_OPTIONS = Object.entries(formOptions)
  .filter(
    ([key]) =>
      !['collection', 'suit_2_pieces', 'suit_3_pieces', 'default'].includes(key)
  )
  .map(([key, opt]) => ({ key, label: opt.name }))

const FixedDesignList = () => {
  const params = useSearchParams()
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role] ? ROLE_PERMISSIONS[user.role] : []

  const storesList = user?.stores
    ? (user.franchiseStore
        ? user.stores.filter((s) => s.store === user.currentStore)
        : user.stores)
    : []
  const storeFilterOptions = storesList.map((store) => ({
    label: storeNameLabel(store.name),
    key: store.store,
  }))

  const filters = (history) => ({
    fields: [
      {
        type: 'select',
        label: 'Store',
        key: 'store',
        default: user?.franchiseStore ? user?.currentStore : '',
        options: storeFilterOptions,
      },
      { type: 'text', label: 'SKU', key: 'sku' },
      { type: 'text', label: 'Name (EN)', key: 'name.en' },
      { type: 'text', label: 'Name (中文)', key: 'name.zh' },
      {
        type: 'select',
        label: 'Category',
        key: 'category',
        default: '',
        options: CATEGORY_FILTER_OPTIONS,
      },
    ],
    actions: [
      {
        label: '+ Create fixed design',
        isActive: permissions.includes('fixed-designs') || permissions.includes('fixed-sku'),
        action: (keyRequest) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) currentPage = parseInt(allFilters._page, 10)
            } catch (e) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) currentPage = parseInt(pageParam, 10)
          }
          history.push(
            createUrlWithFiltersFromKeyRequest('/fixed-designs/create', keyRequest, currentPage)
          )
        },
      },
    ],
  })

  const columns = (history, keyRequest) => [
    {
      title: 'SKU',
      key: 'sku',
      dataIndex: 'sku',
      width: 120,
      sorter: true,
      render: (v) => v ?? '–',
    },
    {
      title: 'Name (EN)',
      key: 'name.en',
      dataIndex: ['name', 'en'],
      sorter: true,
      render: (_, record) => nameLabel(record.name?.en) || '–',
    },
    {
      title: 'Name (中文)',
      key: 'name.zh',
      dataIndex: ['name', 'zh'],
      sorter: true,
      render: (_, record) => nameLabel(record.name?.zh) || '–',
    },
    {
      title: 'Store',
      key: 'store',
      dataIndex: ['store', 'name'],
      width: 120,
      sorter: true,
      render: (_, record) => storeNameLabel(record.store?.name) || '–',
    },
    {
      title: 'Category',
      key: 'category',
      dataIndex: ['details', 0],
      width: 140,
      sorter: true,
      render: (_, record) => {
        const categoryKey = getCategoryFromDetails(record.details)
        return categoryKey ? (formOptions[categoryKey]?.name ?? categoryKey) : '–'
      },
    },
    {
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
      ellipsis: true,
      render: (_, record) => {
        const d = record.description
        const text = typeof d === 'string' ? d : (d?.en || d?.zh || '')
        return text ? (text.length > 50 ? `${text.slice(0, 50)}…` : text) : '–'
      },
    },
    {
      title: '',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const pushUrl = (path) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) currentPage = parseInt(allFilters._page, 10)
            } catch (err) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) currentPage = parseInt(pageParam, 10)
          }
          history.push(createUrlWithFiltersFromKeyRequest(path, keyRequest, currentPage))
        }
        return (
          <Space>
            <Button type="primary" onClick={() => pushUrl(`/fixed-designs/${record.id}`)}>
              Edit
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <PageHeader className="site-page-header" title="Fixed designs" />
      <TableBlock
        columns={columns}
        filters={filters}
        name="getFixedDesigns"
        request={getFixedDesigns}
        otherFilters={
          user?.franchiseStore && user?.currentStore
            ? { store: user.currentStore }
            : {}
        }
      />
    </>
  )
}

export default FixedDesignList
